import { computed } from 'vue'
import { get, find, map, filter, omit, sortBy, includes } from 'lodash'
import {
  contentBlockTypeConfigs,
  appointmentContentActionsConfig,
  defaultNotificationConfig,
  defaultOnlyContentBlockTypes,
} from '@/config/progressNotification'
import { useRoute } from 'vue-router/composables'
import { useShop } from '@/use/shop'
import { passValue } from '@/utils/helper'

export const useProgressNotificationEdit = () => {
  const route = useRoute()
  const { shopId } = useShop()
  const notifyType = computed(() => route.params.type)
  const targetId = computed(() => get(route.params, 'id'))

  const composeSubmitData = (formData) => {
    const data = {
      shopId: shopId.value,
      id: targetId.value,
      type: formData.type,
      NotifyTargetTemplateId: formData.NotifyTargetTemplateId,
      NotifySettings: [],
    }

    data.NotifySettings = map(formData.NotifySettings, (notify, idx) => {
      const baseData = omit(notify, ['sendTimeSettings'])
      return {
        targetIndex: idx,
        ...baseData,
        ...notify.sendTimeSettings,
      }
    })

    return data
  }

  const newNotifySettingBlock = (overrideData) => {
    const notifySettingData = {
      id: overrideData?.id,
      type: notifyType.value,
      enabled: get(overrideData, 'enabled', false), // 是否啟用
      channels: get(overrideData, 'channels') || ['LINE'], // 發送渠道區塊
      targetCondition: get(overrideData, 'targetCondition') || null, // 附加條件區塊
      // 發送時間區塊
      sendTimeSettings: {
        sendTimeType: get(overrideData, 'sendTimeType') ? get(overrideData, 'sendTimeType') : notifyType.value === 'appointmentStart' ? 'before' : 'now',
        sendTimeUnit: get(overrideData, 'sendTimeUnit') ? get(overrideData, 'sendTimeUnit') : notifyType.value === 'appointmentStart' ? 'hour' : null,
        sendTimeOffset: get(overrideData, 'sendTimeOffset') === 0 ? get(overrideData, 'sendTimeOffset') : (get(overrideData, 'sendTimeOffset') ? get(overrideData, 'sendTimeOffset') : notifyType.value === 'appointmentStart' ? 1 : null),
        sendTimeOffsetSpecified: get(overrideData, 'sendTimeOffsetSpecified') || null,
        limitScheduledMinHour: get(overrideData, 'limitScheduledMinHour') === 0 ? 0 : get(overrideData, 'limitScheduledMinHour') || null,
      },
      // 通知內容
      contents: get(overrideData, 'contents') || [],
    }

    return notifySettingData
  }

  // enabled 必須是明確的 boolean：後端 contents 的 Joi 規則要求必填，
  // 若帶 undefined 會被 JSON.stringify 丟掉，導致預覽與儲存都回 400。
  // Boolean() 同時涵蓋未帶值（undefined）與帶 null 兩種呼叫方式，不再另設預設參數
  const newContentData = ({ contentType, order, typeConfig, notifyType, notifyContentTemplateId, enabled }) => {
    const contentData = {
      sameDefaultAll: false,
      enabled: Boolean(enabled),
      type: contentType,
      order,
      NotifyContentTemplateId: notifyContentTemplateId,
    }
    if (contentType === contentBlockTypeConfigs.appointmentInfo.value) {
      contentData.appointmentInfoConfig = {
        title: '標題',
        actions: notifyType === 'appointmentCancel' ? [] : get(appointmentContentActionsConfig, notifyType),
        enabledTitle: true,
      }
    }
    if (typeConfig) contentData[typeConfig.key] = typeConfig.data
    return contentData
  }

  // 依設定頁的區塊順序整理通知內容：只保留該通知類型的區塊，
  // 預設通知補上既有資料沒有的帳號資訊、推薦加購
  const syncContentsWithConfig = ({ notifyType, contents, isDefault }) => {
    const order = defaultNotificationConfig[notifyType] || []
    const newContents = []
    let upsellAdded = false
    for (const item of order) {
      if (includes(defaultOnlyContentBlockTypes, item.type) && !isDefault) continue
      let match = find(contents, content => content.type === item.type)
      if (!match && item.type === contentBlockTypeConfigs.memberInfo.value) {
        match = {
          enabled: true,
          order: 3,
          sameDefaultAll: false,
          type: contentBlockTypeConfigs.memberInfo.value,
        }
      }
      if (!match && item.type === contentBlockTypeConfigs.upsellRecommendation.value) {
        match = {
          enabled: false,
          sameDefaultAll: false,
          type: contentBlockTypeConfigs.upsellRecommendation.value,
        }
        upsellAdded = true
      }
      if (match) newContents.push(match)
    }

    const result = passValue(newContents)
    // 補上的推薦加購沒有 order，依設定頁順序重排
    if (upsellAdded) result.forEach((content, idx) => { content.order = idx + 1 })
    return sortBy(result, 'order')
  }

  const findDefaultContentTemplateData = ({ templateList, contentType, notifyType }) => {
    const template = find(templateList, (template) => template.defaultKey === `${contentType}:${notifyType}`)
    return template
  }

  const filterContentTemplateOptions = ({ templateDataList, contentType }) => {
    const data = map(templateDataList, template => {
      const isDefault = template.isDefault
      const type = template.type
      const isImageText = type === contentBlockTypeConfigs.imageText.value

      const label = isImageText ? `圖文資訊範本 - ${template.name}` : template.name

      return {
        type,
        value: template.id,
        label: isDefault ? `系統預設 - ${label}` : label,
        data: template,
      }
    })
    return filter(data, i => i.type === contentType)
  }

  const selectDefaultContentTemplate = ({ templateDataList, contentType, notifyType }) => {
    if (contentType === contentBlockTypeConfigs.imageText.value) {
      const target = find(templateDataList, { defaultKey: `${contentType}:${renderNotifyType(notifyType)}` })
      if (target) return target.id
      else return null
    }
    const target = find(templateDataList, { isDefault: true, type: contentType })
    if (target) return target.id
    return null
  }
  const renderNotifyType = (type) => {
    switch (type) {
    case 'appointmentStart':
      return 'serviceBefore'
    case 'appointmentEnd':
      return 'serviceAfter'
    case 'appointmentCancel':
      return 'serviceCancel'
    }
  }
  return {
    targetId,
    composeSubmitData,
    newNotifySettingBlock,
    newContentData,
    syncContentsWithConfig,
    findDefaultContentTemplateData,
    filterContentTemplateOptions,
    selectDefaultContentTemplate,
  }
}
